import { select } from '@clack/prompts'
import { Command } from 'commander'
import { z } from 'zod'

import { BaseCollections } from '@scalar-org/entities/database'
import { sdkSchema } from '@scalar-org/entities/sdk'
import { teamSchema } from '@scalar-org/entities/team'
import {
  docVersionSchema,
  namespaceSchema,
  slugSchema,
} from '@scalar-org/entities/utility'

import { apiService, getAuthData, getDatabaseClient } from '@/helpers'
import { output } from '@/helpers/std-out'

export const BuildSdkCommand = () => {
  const cmd = new Command('build')

  cmd.description('Create an SDK build.')

  cmd.option('-s, --slug <slug>', 'SDK slug')
  cmd.option('-n, --namespace <namespace>', 'Team namespace')
  cmd.option('-v, --version <version>', 'SDK Version')

  cmd.action(async (opts) => {
    const { error, data } = z
      .object({
        slug: slugSchema.optional(),
        namespace: namespaceSchema.optional(),
        version: docVersionSchema.optional(),
      })
      .safeParse(opts)

    if (error)
      return output
        .error()
        .title('Invalid input')
        .message(error.issues[0].message)
        .exit('error')

    const auth = await getAuthData()
    auth.addEventListener((state) => Object.assign(auth, state))

    const db = await getDatabaseClient()

    const client = await apiService()

    const team = await db.getOne({
      collectionPath: BaseCollections.Teams,
      query: {
        uid: auth.teamUid,
      },
      schema: teamSchema,
    })

    if (team.error)
      return output
        .error()
        .title('An error occurred when fetching your team.')
        .message(team.message)
        .exit('error')

    /**
     * Most teams will have 1 namespace, so we default to that.
     * Ideally we should also have a global cli namespace selected.
     */
    const namespace = data.namespace || team.data.namespaces.at(0)

    if (!namespace) {
      return output
        .error()
        .title('Missing namespace')
        .message(
          'Could not find a valid namespace for your SDK. Please specify a namespace or contact support.',
        )
        .exit('error')
    }

    const sdks = await db.getMany({
      collectionPath: BaseCollections.Sdks(team.data.uid),
      query: {
        namespace,
      },
      schema: sdkSchema.array(),
    })

    if (sdks.error) return output.error().title(sdks.message).exit('error')

    const sdkUid =
      sdks.data.find((sdk) => sdk.slug === data.slug)?.uid ||
      ((await select({
        message: 'Please select an SDK to update',
        options: sdks.data.map((sdk) => ({
          value: sdk.uid,
          label: sdk.slug,
        })),
      })) as string)

    const matchingSdk = sdks.data.find((sdk) => sdk.uid === sdkUid)

    if (!matchingSdk) {
      return output
        .error()
        .title('No SDK found')
        .message('Please provide a valid SDK slug')
        .exit('error')
    }

    const version =
      matchingSdk.versions.find((v) => v.version === data.version)?.version ||
      ((await select({
        message: 'Please select an SDK version to build',
        options: matchingSdk.versions.map((version) => ({
          value: version.version,
          label: `v${version.version}`,
        })),
      })) as string)

    const logger = output.info().loader('Creating SDK Build')

    const response = await client.sdks.build(sdkUid, version)

    if (response.error) {
      return output
        .error()
        .title('Something went wrong')
        .message(response.message)
        .exit('error')
    }

    logger
      .update('loader', 0, {
        content: `SDK build kicked off for version v${version}`,
      })
      .exit('success')
  })

  return cmd
}
